import React, { createContext } from 'react';
import { Link } from 'react-router-dom';
import { Text } from 'grommet';
import { startCase } from 'lodash';
import { authorizedFetch } from '../utils';
import { NO_VALUE } from '../reports/report-renderer';

export const ItemNameContext = createContext({});

export const ITEM_GROUP_FIELDS = [
  'userSegments',
  'flowId',
  'pageId',
  'advertiserId',
  'campaignId',
  'adGroupId',
  'adCreativeId',
  'campaignVerticalId',
  'campaignSubVerticalId',
  'sourceVerticalId',
  'sourceSubVerticalId',
  'businessUnitId',
  'sortStrategy',
  'subpartnersubverticalid',
  'subpartnerverticalid',
  'placementexperienceid',
  'placementlocationid',
  'configid'
];

export const getEventTypeBucket = (bucket, eventType) => {
  return bucket && bucket.eventType.buckets.find(b => b.key === eventType);
};

export const getEventTargetBucket = (bucket, eventTarget) => {
  return bucket && bucket.eventTarget.buckets.find(b => b.key === eventTarget);
};

export const getEventTypeByTargetBucket = (
  dayBucket,
  eventType,
  targetType
) => {
  const eventTypeBucket = getEventTypeBucket(dayBucket, eventType);
  if (eventTypeBucket) {
    return getEventTargetBucket(eventTypeBucket, targetType);
  }
};

export const getEventTypeCountByTarget = (dayBucket, eventType, targetType) => {
  const eventTargetBucket = getEventTypeByTargetBucket(
    dayBucket,
    eventType,
    targetType
  );
  if (eventTargetBucket) {
    return eventTargetBucket.doc_count;
  }

  return 0;
};

export const getGroupProperty = group => {
  const groupParts = group.split('.');

  //if keyword field mapping, return part without keyword
  if (
    groupParts.length > 1 &&
    groupParts[groupParts.length - 1] === 'keyword' &&
    groupParts[groupParts.length - 2] !== 'queryParams'
  ) {
    return groupParts[groupParts.length - 2];
  } else if (groupParts.length > 1) {
    //use last part of group in dot notation
    return groupParts[groupParts.length - 1];
  }
  return group;
};

const getGroupColumnHeader = property => {
  switch (property) {
    case 'timeStamp':
      return 'Date';
    case 'timeStampHourly':
      return 'Hour';
    case 'userSegments':
      return 'Audience';
    case 'flowId':
      return 'Flow';
    case 'campaignId':
      return 'Campaign';
    case 'advertiserId':
      return 'Advertiser';
    case 'pageId':
      return 'Page';
    case 'osName':
      return 'Device Type';
    case 'origin':
      return 'Delivery Destination';
    case 'sourceid':
      return 'Source ID';
    case 'flowImpressionPosition':
      return 'Campaign Position';
    case 'businessUnitId':
      return 'Business Unit';
    case 'campaignVerticalId':
      return 'Campaign Vertical';
    case 'campaignSubVerticalId':
      return 'Campaign Subvertical';
    case 'sourceVerticalId':
      return 'Source Vertical';
    case 'sourceSubVerticalId':
      return 'Source Subvertical';
    case 'trafficPartnerId':
      return 'Traffic Partner';
    case 'inferredDeviceType':
      return 'Targeted as Device';
    case 'subpartnerid':
      return 'Sub Partner Id';
    case 'subpartnername':
      return 'Sub Partner Name';
    case 'subpartnerverticalid':
      return 'Sub Partner Vertical Id';
    case 'subpartnersubverticalid':
      return 'Sub Partner Subvertical Id';
    case 'placementexperienceid':
      return 'Placement Experience';
    case 'placementlocationid':
      return 'Placement Location';
    case 'offerTitle':
      return 'Creative Title';
    case 'offerDescription':
      return 'Creative Description';
    case 'engagementMethod':
      return 'Engagement Method';
    case 'configid':
      return 'Partner Config';
    default:
      return startCase(property);
  }
};

export const getGroupSortProperty = group => {
  const property = getGroupProperty(group);
  return ITEM_GROUP_FIELDS.indexOf(property) !== -1
    ? `${property}.name`
    : property;
};

export const groupColumn = (group, groups, formatter) => {
  const property = getGroupProperty(group);
  const sortProperty = getGroupSortProperty(group);
  const groupColumnIndex = groups.indexOf(group);
  const subsequentGroup = groups[groupColumnIndex + 1];
  let subsequentGroupProperty;
  if (subsequentGroup) {
    subsequentGroupProperty = getGroupProperty(subsequentGroup);
  }

  return {
    getStyle: row => {
      const style = {
        backgroundColor: '#fff'
      };

      if (!subsequentGroupProperty || row[subsequentGroupProperty] === 'All') {
        style.borderTop = 'solid 1px #DADADA';
      }
      return style;
    },
    property: property,
    header: () => (
      <Text style={{ whiteSpace: 'normal', fontSize: 'inherit' }}>
        {getGroupColumnHeader(property)}
      </Text>
    ),
    render: row => (
      <GroupCell
        group={group}
        row={row}
        property={property}
        subsequentGroupProperty={subsequentGroupProperty}
        formatter={formatter}
      />
    ),
    sort: sortProperty,
    defaultOrder: 'desc'
  };
};

const GroupCell = ({ row, property, subsequentGroupProperty, formatter }) => {
  let innerText;

  //lookup item name map
  if (row[property] !== 'All') {
    innerText = row[`${property}.name`] || row[property];
  }
  if (!innerText) {
    innerText = row[property];
  }

  const renderGroupCellText = () => (
    <Text
      title={innerText}
      truncate
      weight={row[property] === 'All' ? 'bold' : 'normal'}
      style={{
        opacity:
          !subsequentGroupProperty || row[subsequentGroupProperty] === 'All'
            ? '100%'
            : 0,
        fontStyle: row[property] === NO_VALUE ? 'italic' : 'normal'
      }}
    >
      {typeof formatter === 'function' ? formatter(innerText) : innerText}
    </Text>
  );

  const urlPath = row[`${property}.urlPath`];

  return urlPath ? (
    <Link
      to={urlPath}
      style={{
        color: 'inherit',
        pointerEvents:
          row[property] === NO_VALUE || row[property] === 'All'
            ? 'none'
            : 'pointer',
        textDecoration:
          row[property] === NO_VALUE || row[property] === 'All'
            ? 'none'
            : 'underline',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        visibility:
          !subsequentGroupProperty || row[subsequentGroupProperty] === 'All'
            ? 'visible'
            : 'hidden'
      }}
      target='_blank'
    >
      {renderGroupCellText()}
    </Link>
  ) : (
    renderGroupCellText()
  );
};

export const getAudiences = (audienceIds, setAudienceNameMap, addError) => {
  authorizedFetch(`/api/search/eventflow-audiencereadmodel/_search`, 'POST', {
    _source: ['id', 'name'],
    query: {
      terms: {
        'id.keyword': audienceIds
      }
    }
  })
    .then(response => {
      let audienceIdNameMap = {};
      response.hits.hits.reduce((acc, curr) => {
        acc[curr._source.id] = curr._source.name;
        return acc;
      }, audienceIdNameMap);
      setAudienceNameMap(audienceIdNameMap);
    })
    .catch(e => addError('Unable to get audiences', JSON.stringify(e)));
};
